import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ApprovalRequest } from './ApprovalsPanel';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface VmSummary { powerState: string }
interface JobSummary {
  id: string; name: string; status: string; nextRunAt?: string;
  recentLogs: { executedAt: string; success: boolean; message: string }[];
}
interface AuditEventSummary {
  id: string; timestampUtc: string; user: string; category: string;
  action: string; target: string; success: boolean; dryRun: boolean;
}

interface OverviewProps {
  onNavigate: (tab: string) => void;
}

const OpsOverview: React.FC<OverviewProps> = ({ onNavigate }) => {
  const [vms, setVms] = useState<VmSummary[]>([]);
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [audit, setAudit] = useState<AuditEventSummary[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    // Each panel degrades independently — one failing API must not blank the whole overview.
    const [vmsRes, jobsRes, auditRes, approvalsRes] = await Promise.allSettled([
      axios.get(`${API_URL}/vm`, { params: { subscriptionId: 'all' } }),
      axios.get(`${API_URL}/scheduler/jobs`),
      axios.get(`${API_URL}/audit`, { params: { limit: 100 } }),
      axios.get(`${API_URL}/approvals`, { params: { status: 'Pending' } }),
    ]);
    if (vmsRes.status === 'fulfilled') setVms(vmsRes.value.data);
    if (jobsRes.status === 'fulfilled') setJobs(jobsRes.value.data);
    if (auditRes.status === 'fulfilled') setAudit(auditRes.value.data);
    if (approvalsRes.status === 'fulfilled') setPendingApprovals(approvalsRes.value.data);
    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const running = vms.filter(v => v.powerState === 'Running').length;
  const stopped = vms.filter(v => v.powerState === 'Stopped').length;
  const deallocated = vms.filter(v => v.powerState === 'Deallocated').length;

  const activeJobs = jobs.filter(j => j.status === 'Active');
  const dayAgo = Date.now() - 24 * 3600 * 1000;
  const failedRuns24h = jobs.flatMap(j =>
    j.recentLogs.filter(l => !l.success && new Date(l.executedAt).getTime() > dayAgo)
      .map(l => ({ job: j.name, ...l }))
  ).sort((a, b) => new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime());

  const failedAudit24h = audit.filter(e => !e.success && new Date(e.timestampUtc).getTime() > dayAgo);

  const nextRuns = activeJobs
    .filter(j => j.nextRunAt)
    .sort((a, b) => new Date(a.nextRunAt!).getTime() - new Date(b.nextRunAt!).getTime())
    .slice(0, 5);

  const card = (label: string, value: React.ReactNode, color: string, tab: string, sub?: string) => (
    <div
      onClick={() => onNavigate(tab)}
      style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '1rem', cursor: 'pointer', minWidth: 150, flex: 1 }}
    >
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ color: '#888', fontSize: '0.85rem', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ color: '#666', fontSize: '0.75rem', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const panel = (title: string, children: React.ReactNode) => (
    <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '1rem 1.25rem', flex: 1, minWidth: 320 }}>
      <div style={{ color: '#888', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>{title}</div>
      {children}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <p style={{ margin: 0, color: '#888', fontSize: '0.9rem' }}>
          Environment at a glance — cards navigate to the relevant view. Auto-refreshes every 30s.
        </p>
        <span style={{ color: '#666', fontSize: '0.8rem' }}>
          {loading ? 'Refreshing…' : lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : ''}
        </span>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {card('VMs running', running, '#28a745', 'dashboard', `${stopped} stopped · ${deallocated} deallocated`)}
        {card('Stopped (still billing)', stopped, stopped > 0 ? '#ffc107' : '#888', 'dashboard', stopped > 0 ? 'Deallocate to stop charges' : undefined)}
        {card('Pending approvals', pendingApprovals.length, pendingApprovals.length > 0 ? '#ffc107' : '#888', 'approvals')}
        {card('Failed job runs (24h)', failedRuns24h.length, failedRuns24h.length > 0 ? '#dc3545' : '#888', 'scheduler', `${activeJobs.length} active jobs`)}
        {card('Failed operations (24h)', failedAudit24h.length, failedAudit24h.length > 0 ? '#dc3545' : '#888', 'audit')}
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {panel('Needs attention',
          failedRuns24h.length === 0 && failedAudit24h.length === 0 && pendingApprovals.length === 0 ? (
            <div style={{ color: '#7ee2a8', fontSize: '0.9rem' }}>✓ All clear — no failures or pending decisions in the last 24h.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
              {pendingApprovals.slice(0, 3).map(a => (
                <div key={a.id} style={{ color: '#ffe08a' }}>
                  ⏳ Approval pending: {a.targetSummary} <span style={{ color: '#888' }}>({a.requestedBy})</span>
                </div>
              ))}
              {failedRuns24h.slice(0, 3).map((f, i) => (
                <div key={i} style={{ color: '#ff8f9a' }}>
                  ✗ Job "{f.job}": {f.message} <span style={{ color: '#888' }}>{new Date(f.executedAt).toLocaleTimeString()}</span>
                </div>
              ))}
              {failedAudit24h.slice(0, 3).map(e => (
                <div key={e.id} style={{ color: '#ff8f9a' }}>
                  ✗ {e.category}/{e.action}: {e.target} <span style={{ color: '#888' }}>({e.user})</span>
                </div>
              ))}
            </div>
          )
        )}

        {panel('Upcoming scheduled runs',
          nextRuns.length === 0 ? (
            <div style={{ color: '#888', fontSize: '0.9rem' }}>No active scheduled jobs.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
              {nextRuns.map(j => (
                <div key={j.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: '#f0f0f0' }}>{j.name}</span>
                  <span style={{ color: '#888', flexShrink: 0 }}>{new Date(j.nextRunAt!).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )
        )}

        {panel('Recent activity',
          audit.length === 0 ? (
            <div style={{ color: '#888', fontSize: '0.9rem' }}>No recorded operations yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
              {audit.slice(0, 6).map(e => (
                <div key={e.id} style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: e.success ? '#28a745' : '#dc3545', flexShrink: 0 }}>{e.success ? '✓' : '✗'}</span>
                  <span style={{ color: '#b0b0b0', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.action} — {e.target}{e.dryRun ? ' (dry run)' : ''}
                  </span>
                  <span style={{ color: '#666', flexShrink: 0 }}>{e.user}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default OpsOverview;
